import React, { useEffect, useState } from 'react';
import { GroupBase, OptionProps } from 'react-select';
import { useCollapse } from 'react-collapsed';
import styled from 'styled-components';
import { SvgIcon } from '../../../../Icon';
import { NEW_DASHBOARD_SETTINGS } from '../utils';
import { CustomOption } from '../types';
import { StyledSingleSelectOption } from '../../../../Dropdown/SingleSelect';

const AccordionOption = styled(StyledSingleSelectOption)`
  padding-left: 32px;
`;

function DashboardAccordionOption(
  props: OptionProps<CustomOption, false, GroupBase<CustomOption>> & {
    searchTerm: string;
  },
) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

  const toggleAccordion = (e: React.MouseEvent) => {
    // Prevents react-select from closing the dropdown
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleSubOptionClick = (subOption: CustomOption) => {
    props.selectOption({
      ...props.data,
      value: subOption.value,
      label: subOption.label,
    });
  };

  // Automatically expand if the search term matches the main option or any sub-option
  useEffect(() => {
    if (props.searchTerm.length > 0) {
      const matchesMainOption = props.data.label
        .toLowerCase()
        .includes(props.searchTerm.toLowerCase());

      const matchesSubOption = props.data.subOptions?.some((subOption) =>
        subOption.label.toLowerCase().includes(props.searchTerm.toLowerCase()),
      );

      if (matchesMainOption || matchesSubOption) {
        setIsExpanded(true);
      } else {
        setIsExpanded(false);
      }
    }
  }, [props.searchTerm, props.data]);

  // Filter subOptions based on the search term
  const filteredSubOptions = props.data.subOptions?.filter((subOption) =>
    subOption.label.toLowerCase().includes(props.searchTerm.toLowerCase()),
  );

  if (props.data.value === NEW_DASHBOARD_SETTINGS.key) {
    return (
      <StyledSingleSelectOption
        onClick={() => handleSubOptionClick(props.data)}
      >
        <SvgIcon
          name="plus-circle"
          width={16}
          height={16}
          customFill="currentColor"
        />
        {props.data.label}
      </StyledSingleSelectOption>
    );
  }

  return (
    <>
      <StyledSingleSelectOption
        {...getToggleProps({ onClick: toggleAccordion })}
      >
        {props.data.icon}
        {props.data.label}
        <SvgIcon
          name={isExpanded ? 'caret-down-fill' : 'caret-right-fill'}
          width={10}
          height={10}
          customFill="currentColor"
        />
      </StyledSingleSelectOption>
      {isExpanded && (
        <div {...getCollapseProps()}>
          {filteredSubOptions &&
            filteredSubOptions.map((subOption) => (
              <AccordionOption
                key={subOption.value}
                onClick={() => handleSubOptionClick(subOption)}
                disabled={subOption.isDisabled}
              >
                {subOption.label}
              </AccordionOption>
            ))}
        </div>
      )}
    </>
  );
}

export default DashboardAccordionOption;
